import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { tap } from 'rxjs/operators';

import { USER_ENDPOINTS } from '../api/api-endpoints';
import { RestApiService } from '../api/rest-api.service';
import { TimezoneService } from './timezone.service';
import { S3FileService } from './s3-file.service';
import { retryTransientRead } from '../api/retry-transient-read';

export interface PersonalSettingsRecord {
  id: string;
  email: string;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  date_of_birth: string | null;
  gender: string | null;
  timezone: string;
  preferred_currency_code: string | null;
  country_of_origin_iso2: string | null;
  profile_image_key: string | null;
  email_verified: boolean;
  status: string;
  auth_provider: string;
  password_set: boolean;
  requires_password_setup: boolean;
  created_at: string;
}

export interface PersonalSettingsUpdate {
  first_name: string;
  middle_name: string | null;
  last_name: string;
  phone_number: string | null;
  date_of_birth: string | null;
  gender: string | null;
  timezone: string;
  preferred_currency_code: string | null;
  country_of_origin_iso2: string | null;
  profile_image_key: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class PersonalSettingsService {
  private readonly currentSubject = new BehaviorSubject<PersonalSettingsRecord | null>(null);
  private readonly avatarUrlSubject = new BehaviorSubject<string | null>(null);
  private avatarResolutionVersion = 0;

  readonly current$ = this.currentSubject.asObservable();
  readonly avatarUrl$ = this.avatarUrlSubject.asObservable();

  constructor(
    private readonly api: RestApiService,
    private readonly timezone: TimezoneService,
    private readonly files: S3FileService,
  ) {}

  get(): Observable<PersonalSettingsRecord> {
    return this.api
      .get<PersonalSettingsRecord>(USER_ENDPOINTS.personal_settings)
      .pipe(retryTransientRead(),tap((record) => this.publish(record)));
  }

  update(payload: PersonalSettingsUpdate): Observable<PersonalSettingsRecord> {
    return this.api
      .patch<PersonalSettingsRecord>(USER_ENDPOINTS.personal_settings, payload)
      .pipe(tap((record) => this.publish(record)));
  }

  private publish(record: PersonalSettingsRecord): void {
    this.timezone.setUserTimezone(record.timezone);
    this.currentSubject.next(record);

    const version = ++this.avatarResolutionVersion;
    if (!record.profile_image_key) {
      this.avatarUrlSubject.next(null);
      return;
    }

    this.files.getFileUrlValue(record.profile_image_key).pipe(take(1)).subscribe((url) => {
      if (version === this.avatarResolutionVersion) {
        this.avatarUrlSubject.next(url);
      }
    });
  }
}
